<template>
  <div v-if="i18nReady" class="category-filter" :class="classModifiers">
    <sp-quantum-select
      :aria-label="label"
      class="category-filter__select"
      :navigation="navigation"
      v-bind="branchAttrs"
      :multiple="isMultiple"
      :checkboxes="isMultiple"
      :close-on-select="!isMultiple"
      clearable
      :hide-search-bar="isSearchHidden"
      :show-search-bar-in-sheet="!isSearchHidden"
      :title="listTitle"
      :items.prop="models"
      :selected.prop="selectedRowValues"
      :location="location"
      :open="open"
      :required="isNonClearable"
      :pointer="showPointer"
      :placeholder="t('search_placeholder')"
      :no-data-text="t('no_results')"
      :input-test-id="searchInputTestId"
      :item-test-id="rowTestId"
      :branch-toggle-test-id="branchToggleTestId"
      @update-selected="handleUpdateSelected"
      @update-open="handleUpdateOpen"
    >
      <FilterActivatorSlot
        :label="label"
        :text="selectedText"
        :prepend-icon="prependIcon"
        :append-icon="appendIcon"
        :open="open"
        :non-clearable="isNonClearable"
        @clear="clear"
      >
        <template v-if="$slots.activator" #activator="activatorProps">
          <slot name="activator" v-bind="activatorProps" />
        </template>
      </FilterActivatorSlot>

      <sp-button
        v-if="showClear"
        slot="navbar-actions"
        class="category-filter__clear"
        variant="text-inline"
        color="primary"
        size="small"
        :data-testid="clearActionTestId"
        @click="clear"
        >{{ t("clear") }}</sp-button
      >
    </sp-quantum-select>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { toBoolean } from "#ce/utils/props";
import { firstDetail } from "#ce/utils/events";
import { TestId } from "#/test-ids";
import { useControllableOpen } from "#ce/shared/filters/composables/useControllableOpen";
import { useCategoryFilterScope } from "#ce/shared/filters/category/composables/useCategoryFilterScope";
import { useCategoryFilterSelection } from "#ce/shared/filters/category/composables/useCategoryFilterSelection";
import { useCategoryFilterItems } from "#ce/shared/filters/category/composables/useCategoryFilterItems";
import { t, i18nReady } from "#ce/shared/filters/i18n";
import { props as categoryFilterProps } from "#ce/shared/filters/category/components/props";
import FilterActivatorSlot from "#ce/shared/filters/components/FilterActivatorSlot.ce.vue";

const emit = defineEmits(["update:model-value", "update:open"]);

const props = defineProps(categoryFilterProps);

const { selectedIds, selectedRowValues, offersAny, isMultiple, hasSelection, handleUpdateSelected, clear } =
  useCategoryFilterSelection(props, emit);

const { setScope, isScoped, isInScope } = useCategoryFilterScope({ name: props.name, url: props.url });

const { branchAttrs, models, nodesById } = useCategoryFilterItems(props, selectedIds, {
  isScoped,
  isInScope,
  offersAny,
});

const searchInputTestId = computed(() => props.searchTestId || TestId.events.categoryFilterSearch);
const clearActionTestId = computed(() => props.clearTestId || TestId.events.categoryFilterClear);
const rowTestId = computed(() => props.optionTestId || TestId.events.categoryFilterOption);
const listTitle = computed(() => props.menuTitle || props.label);
const isNonClearable = computed(() => toBoolean(props.nonClearable));
const canClear = computed(() => !isNonClearable.value);
const isSearchHidden = computed(() => toBoolean(props.noSearch));
const showPointer = computed(() => !toBoolean(props.hidePointer));
const showClear = computed(() => hasSelection.value && canClear.value);

/** The activator text: the one selected category's name, or the count while several are selected. */
const selectedText = computed(() => {
  const count = selectedIds.value.length;

  if (count === 0) {
    return undefined;
  }
  if (count === 1) {
    return nodesById.value.get(selectedIds.value[0])?.name ?? selectedIds.value[0];
  }

  return t("multiple_selected", { count });
});

const open = useControllableOpen(() => props.open, emit);

function handleUpdateOpen(event) {
  open.value = firstDetail(event);
}

const classModifiers = computed(() => ({
  "--has-selected-model": hasSelection.value,
  "--no-search": isSearchHidden.value,
}));

defineExpose({ props, active: hasSelection, selectedText, setScope });
</script>

<style>
:host {
  --filter-width: 100%;
  display: block;
  box-sizing: border-box;
}
:host([chrome="bar"]) {
  --filter-width: max-content;
  --sp-comp-popup-width: var(--sp-comp-filter-bar-popup-width, 18.5rem);
}
</style>

<style lang="scss" scoped>
.category-filter {
  --sp-comp-button-width: var(--filter-width);

  display: flex;
  align-items: center;
  height: 100%;
  width: var(--filter-width);

  &__select {
    height: 100%;
    width: var(--filter-width);
  }

  // The button host is a block; flex centres its inline button in the title bar.
  &__clear {
    display: flex;
  }

  .activator {
    width: var(--filter-width);
  }

  // A list with no search box keeps one length, so its panel fits the rows.
  &.--no-search {
    --sp-comp-quantum-select-popup-min-height: 0;
    --sp-comp-quantum-select-popup-max-height: 25rem;
  }

  &.--has-selected-model .activator {
    --sp-comp-text-field-color: var(--sp-sys-color-on-surface);
  }
}
</style>
